// Field Core Web Vitals (RUM) for the public website. CrUX only reports the p75 per origin/URL;
// the attribution build adds what made a page slow (LCP element and its load phases, INP target,
// largest layout shift), so field regressions can be traced to a page and a cause.
// Each metric is sent once per page view as a `web-vitals` custom event to the App Insights browser
// SDK that the App Service agent injects (window.appInsights); without it nothing is collected.
// Query: customEvents | where name == "web-vitals" (numbers in customMeasurements, sum(itemCount) for counts).

import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals/attribution';
import type { MetricWithAttribution } from 'web-vitals/attribution';

type AppInsights = {
	trackEvent: (event: {
		name: string;
		properties?: Record<string, string>;
		measurements?: Record<string, number>;
	}) => void;
};

// The participant app and the backoffice are SPAs with their own load profile, not the website.
const EXCLUDED_PATHS = /^\/(app|umbraco)(\/|$)/i;

// Media URLs carry the crop (width/height/quality), which identifies the srcset candidate; drop the host and cache buster.
function shortUrl(url: string | undefined): string | undefined {
	if (!url) {
		return undefined;
	}

	try {
		const parsed = new URL(url, location.href);
		parsed.searchParams.delete('v');
		return parsed.origin === location.origin ? parsed.pathname + parsed.search : parsed.href;
	} catch {
		return url;
	}
}

function details(metric: MetricWithAttribution): {
	properties: Record<string, string | undefined>;
	measurements: Record<string, number | undefined>;
} {
	switch (metric.name) {
		case 'LCP': {
			const a = metric.attribution;
			return {
				properties: { target: a.target, resource: shortUrl(a.url) },
				measurements: {
					timeToFirstByte: a.timeToFirstByte,
					resourceLoadDelay: a.resourceLoadDelay,
					resourceLoadDuration: a.resourceLoadDuration,
					elementRenderDelay: a.elementRenderDelay,
				},
			};
		}
		case 'INP': {
			const a = metric.attribution;
			return {
				properties: { target: a.interactionTarget, interactionType: a.interactionType, loadState: a.loadState },
				measurements: {
					inputDelay: a.inputDelay,
					processingDuration: a.processingDuration,
					presentationDelay: a.presentationDelay,
				},
			};
		}
		case 'CLS': {
			const a = metric.attribution;
			return {
				properties: { target: a.largestShiftTarget, loadState: a.loadState },
				measurements: { largestShiftValue: a.largestShiftValue },
			};
		}
		case 'FCP': {
			const a = metric.attribution;
			return {
				properties: { loadState: a.loadState },
				measurements: { timeToFirstByte: a.timeToFirstByte, firstByteToFCP: a.firstByteToFCP },
			};
		}
		case 'TTFB': {
			const a = metric.attribution;
			return {
				properties: {},
				measurements: {
					waitingDuration: a.waitingDuration,
					cacheDuration: a.cacheDuration,
					dnsDuration: a.dnsDuration,
					connectionDuration: a.connectionDuration,
					requestDuration: a.requestDuration,
				},
			};
		}
	}
}

function compact<T>(record: Record<string, T | undefined>): Record<string, T> {
	return Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined)) as Record<string, T>;
}

// TTFB and FCP fire early; should App Insights only arrive later (e.g. through the deferred GTM), they are
// held back and sent with the next metric. LCP, CLS and INP are reported when the page is hidden.
const pending: MetricWithAttribution[] = [];

function report(metric: MetricWithAttribution): void {
	const appInsights = (window as unknown as { appInsights?: AppInsights }).appInsights;
	if (!appInsights?.trackEvent) {
		pending.push(metric);
		return;
	}

	pending.splice(0).forEach((earlier) => track(appInsights, earlier));
	track(appInsights, metric);
}

function track(appInsights: AppInsights, metric: MetricWithAttribution): void {
	const connection = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection;
	const { properties, measurements } = details(metric);
	appInsights.trackEvent({
		name: 'web-vitals',
		properties: compact({
			metric: metric.name,
			rating: metric.rating,
			navigationType: metric.navigationType,
			path: location.pathname,
			viewport: `${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio}`,
			effectiveType: connection?.effectiveType,
			...properties,
		}),
		measurements: compact({ value: metric.value, ...measurements }),
	});
}

export function initWebVitals(): void {
	if (EXCLUDED_PATHS.test(location.pathname)) {
		return;
	}

	onLCP(report);
	onINP(report);
	onCLS(report);
	onFCP(report);
	onTTFB(report);
}
